import cn from 'classnames';
import { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';

export interface BaseButtonProps {
  text?: ReactNode;
  additionalClass?: string;
  disabled?: boolean;
  icon?: ReactElement;
  iconClass?: string;
  iconPosition?: 'left' | 'right';
  variant?:
    | 'primary'
    | 'success'
    | 'secondary'
    | 'danger'
    | 'info'
    | 'light'
    | 'warning'
    | 'light-primary'
    | 'light-secondary'
    | 'light-danger';
  variantType?: 'filled' | 'outlined';
  size?: 'sm' | 'md' | 'lg';
}

const iconSizeClass: Record<NonNullable<BaseButtonProps['size']>, string> = {
  sm: 'fs-4',
  md: 'fs-3',
  lg: 'fs-2'
};

const iconContainerSize: Record<NonNullable<BaseButtonProps['size']>, string> = {
  sm: '16px',
  md: '18px',
  lg: '20px'
};

export interface ButtonProps extends BaseButtonProps {
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  onClick?: () => void;
  isLoading?: boolean;
}

export const Button = ({
  text = 'Button',
  additionalClass,
  icon,
  iconClass,
  iconPosition = 'left',
  type = 'button',
  disabled,
  isLoading,
  onClick,
  variant = 'primary',
  variantType = 'filled',
  size = 'md'
}: ButtonProps) => {
  const buttonClasses = cn(
    'btn',
    `btn${variantType === 'outlined' ? '-outline' : ''}-${variant}`,
    {
      'd-inline-flex align-items-center justify-content-center': true,
      'btn-sm': size === 'sm',
      'btn-lg': size === 'lg',
      disabled: disabled || isLoading
    },
    additionalClass
  );

  return (
    <button className={buttonClasses} type={type} onClick={onClick} disabled={disabled || isLoading}>
      {(icon || iconClass || isLoading) && (
        <span
          className={cn('d-inline-flex align-items-center justify-content-center', {
            'me-2': iconPosition === 'left',
            'ms-2 order-1': iconPosition === 'right'
          })}
          style={
            iconClass ? { width: iconContainerSize[size ?? 'md'], height: iconContainerSize[size ?? 'md'] } : undefined
          }
        >
          {isLoading ? (
            <span
              className="spinner-border"
              role="status"
              aria-hidden="true"
              style={{ width: iconContainerSize[size ?? 'md'], height: iconContainerSize[size ?? 'md'] }}
            />
          ) : iconClass ? (
            <i className={cn(iconClass, iconSizeClass[size ?? 'md'])} />
          ) : (
            icon
          )}
        </span>
      )}
      {text}
    </button>
  );
};
